/**
 * Inline markup parser for paragraph-level text.
 *
 * The editor deliberately does NOT expose a full rich-text surface — that is
 * how a site drifts into fifteen fonts. Authors get four inline marks and
 * nothing else, and this module turns them into a token tree that the renderer
 * maps onto themed elements.
 *
 * Supported: `**bold**`, `*italic*`, `` `code` ``, `[text](url)`.
 *
 * Layer rule: pure logic — returns data, never JSX, never HTML strings. That
 * keeps it testable and keeps the theme in charge of appearance. Because it
 * never emits HTML, there is no injection surface: the renderer sets text via
 * React children, so author input cannot become markup.
 *
 * 段落级行内标记解析器。
 *
 * 编辑器刻意不提供完整富文本能力——那正是一个站点变成十五种字体的原因。作者只有
 * 四种行内标记，本模块把它们解析成 token 树，由渲染器映射到主题化元素。
 *
 * 支持：**粗体**、*斜体*、`代码`、[文字](链接)。
 *
 * 分层规则：纯逻辑——只返回数据，不产出 JSX，也不产出 HTML 字符串。这样既好测试，
 * 又把外观决定权留给主题。因为从不生成 HTML，也就没有注入面：渲染器通过 React
 * children 输出文本，作者输入无法变成标记。
 */

/**
 * Ordered matchers. Order matters: `**` must be tried before `*`, otherwise a
 * bold run is mis-parsed as two empty italics.
 *
 * 匹配器有序。顺序要紧：必须先试 ** 再试 *，否则粗体会被误解析成两个空斜体。
 */
const MATCHERS = [
  { type: 'code', pattern: /^`([^`]+)`/ },
  { type: 'bold', pattern: /^\*\*([^*]+)\*\*/ },
  { type: 'italic', pattern: /^\*([^*]+)\*/ },
  { type: 'link', pattern: /^\[([^\]]+)\]\(([^)\s]+)\)/ },
]

/**
 * Allow only schemes that cannot execute script.
 *
 * `javascript:` and `data:` URLs in an href are the classic stored-XSS vector
 * for user-authored links, so anything outside this list is dropped and the
 * link degrades to plain text.
 *
 * 只允许无法执行脚本的协议。href 里的 javascript: 和 data: 是用户可编辑链接
 * 最经典的存储型 XSS 途径，因此白名单之外一律丢弃，链接降级为纯文本。
 *
 * @param {string} url Candidate URL / 待检查链接。
 * @returns {boolean} True when safe to render as an anchor / 可安全渲染为链接则为 true。
 */
export function isSafeUrl(url) {
  const trimmed = String(url || '').trim()
  if (!trimmed) return false
  // Relative, root-relative, anchor and query links are always fine.
  // 相对路径、根相对路径、锚点和查询串一律安全。
  if (/^[/#?]/.test(trimmed)) return true
  return /^(https?:|mailto:|tel:)/i.test(trimmed)
}

/**
 * Parse inline markup into a flat token list.
 *
 * 把行内标记解析成扁平 token 列表。
 *
 * @param {string} text Source text / 源文本。
 * @returns {Array<{type: string, text: string, href?: string}>} Tokens / token 列表。
 */
export function parseInline(text) {
  const source = String(text ?? '')
  const tokens = []
  let plain = ''
  let index = 0

  const flushPlain = () => {
    if (plain) {
      tokens.push({ type: 'text', text: plain })
      plain = ''
    }
  }

  while (index < source.length) {
    const rest = source.slice(index)
    let matched = false

    for (const matcher of MATCHERS) {
      const match = rest.match(matcher.pattern)
      if (!match) continue

      flushPlain()
      if (matcher.type === 'link') {
        const [full, label, href] = match
        if (isSafeUrl(href)) {
          tokens.push({ type: 'link', text: label, href: href.trim() })
        } else {
          // Unsafe scheme: keep the words, drop the link.
          // 协议不安全：保留文字，去掉链接。
          tokens.push({ type: 'text', text: label })
        }
        index += full.length
      } else {
        tokens.push({ type: matcher.type, text: match[1] })
        index += match[0].length
      }
      matched = true
      break
    }

    if (!matched) {
      plain += source[index]
      index += 1
    }
  }

  flushPlain()
  return tokens
}

/**
 * Strip all inline markup, e.g. for meta descriptions and list previews.
 *
 * 去掉所有行内标记，用于 meta 描述和列表预览等场景。
 *
 * @param {string} text Source text / 源文本。
 * @returns {string} Plain text / 纯文本。
 */
export function stripInline(text) {
  return parseInline(text)
    .map((token) => token.text)
    .join('')
}

/**
 * Shorten text to a length budget without cutting a word in half.
 *
 * Counts CJK characters as full-width, so a Chinese and an English summary end
 * up looking about the same length on screen.
 *
 * 在不截断单词的前提下把文本压到长度预算内。CJK 字符按全角计数，
 * 这样中英文摘要在屏幕上看起来长度接近。
 *
 * @param {string} text Source text / 源文本。
 * @param {number} [budget] Width budget in half-widths / 半角宽度预算。
 * @returns {string} Possibly ellipsised text / 可能带省略号的文本。
 */
export function truncate(text, budget = 160) {
  const plain = stripInline(text).trim()
  let width = 0
  let cut = plain.length

  for (let i = 0; i < plain.length; i += 1) {
    width += /[⺀-鿿＀-￯]/.test(plain[i]) ? 2 : 1
    if (width > budget) {
      cut = i
      break
    }
  }
  if (cut >= plain.length) return plain

  const slice = plain.slice(0, cut)
  // Only back off to a word boundary for Latin text; CJK has no spaces.
  // 只对拉丁文本回退到词边界；中文没有空格。
  const lastSpace = slice.lastIndexOf(' ')
  const trimmed = lastSpace > budget * 0.6 ? slice.slice(0, lastSpace) : slice
  return `${trimmed.replace(/[\s,;:.，、；：]+$/, '')}…`
}
